import clsx from 'clsx';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import { PrimaryButton, TextButton } from '@components/Button/variants';
import LazyImgixImage from '@components/LazyImgixImage';
import type { ModalHeaderProps } from '@components/MarketingModal/common/modalHeader';
import { TiModalColorScheme } from '@components/TIModal';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';
import { getSafeUrl } from '@utils/safe_url';

export type SingleContentHighlightImageProps = ModalHeaderProps & {
  imageURL?: string;
  mainCTA?: string;
  mainURL?: string;
  modalTheme?: TiModalColorScheme;
  optionalCTA?: string;
  optionalURL?: string;
};

/**
 * Promotional modal for a single piece of content with an image.
 * Growth calls this the Retention P2 LE Modal - Single Content Highlight IMG.
 */
const SingleContentHighlightImage = ({
  highlight,
  imageURL,
  mainCTA,
  mainURL,
  modalTheme,
  optionalCTA,
  optionalURL,
  subtitle,
  title,
}: SingleContentHighlightImageProps) => {
  const { trackEvent } = useEventTracking();

  const safeImageURL = getSafeUrl(imageURL);
  const safeMainURL = getSafeUrl(mainURL, { allowRelative: true });
  const safeOptionalURL = getSafeUrl(optionalURL, { allowRelative: true });

  const handleMainCTAClick = () => {
    trackEvent(
      click({ clickTarget: ClickTarget.AnchorLink, clickText: mainCTA, clickLink: safeMainURL })
    );
  };

  const handleOptionalCTAClick = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.AnchorLink,
        clickText: optionalCTA,
        clickLink: safeOptionalURL,
      })
    );
  };

  const textColor = modalTheme === TiModalColorScheme.dark ? 'text-white' : 'text-primary-black';

  return (
    <div className="flex max-h-[100vh] w-full flex-wrap items-center justify-center overflow-y-auto px-0 pb-0 pt-64 md:py-0 lg:px-0">
      <div className="flex max-w-full flex-wrap items-start md:h-full md:flex-nowrap md:justify-center md:gap-x-48">
        <div className="mt-8 flex flex-col px-32 md:mt-48 md:pb-24">
          {highlight && (
            <span className="block font-suisse-intl-mono text-overline-sm font-normal text-primary-magenta sm:text-overline-md">
              {highlight}
            </span>
          )}
          <h1
            className={clsx(
              'my-0 font-suisse-works text-headline-sm sm:text-headline-lg',
              textColor
            )}
          >
            {title}
          </h1>
          <p
            className={clsx(
              'mb-0 mt-8 font-suisse-intl text-description-sm sm:text-body-lg',
              textColor
            )}
          >
            {ReactHtmlParser(subtitle)}
          </p>
          <div className="mt-24 flex flex-row items-center gap-x-32">
            <PrimaryButton
              as="link"
              className="my-0"
              href={safeMainURL}
              onClickCapture={handleMainCTAClick}
            >
              {mainCTA}
            </PrimaryButton>
            {optionalCTA && safeOptionalURL && (
              <TextButton
                as="link"
                className="my-0"
                href={safeOptionalURL}
                onClickCapture={handleOptionalCTAClick}
              >
                {optionalCTA}
              </TextButton>
            )}
          </div>
        </div>
        <LazyImgixImage
          src={safeImageURL}
          wrapperClassName="w-full h-full md:w-[620px] md:aspect-[500/390] md:flex-shrink-0 overflow-hidden mt-48 sm:mt-28 md:mt-0 lg:aspect-[620/420]"
          className="block aspect-[390/234] h-full w-full object-cover sm:aspect-[500/390] md:aspect-[auto] md:max-h-[50vh_-_40px] lg:max-h-none"
          role="presentation"
          sizes="(min-width: 992px) 620px, (min-width: 768px) 500px, 390px"
        />
      </div>
    </div>
  );
};

export default SingleContentHighlightImage;
